import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { Alert } from "react-bootstrap";
import { connect, useDispatch } from "react-redux";
import uuidv4 from "uuid/v4";
import { Alert as DocuclipperAlert } from "../../../docuclipper/DocuclipperTypes";
import { removeAlert, RemoveAlertFn } from "../../../redux/reducers/Alerts";
import { updateTours } from "../../../redux/reducers/Tours";
import { UpgradeAlertBanner } from "../../../utils/AlertRenders";

type Props = {};

type ReduxProps = {
  alert: DocuclipperAlert;
  removeAlert: RemoveAlertFn;
};

// Default auto-dismiss for success messages — historical call sites pass
// `timeout: 0` everywhere, which was interpreted as "persistent". That's wrong
// for successes: "Data exported correctly" etc. should acknowledge + go away.
// Errors and info stay until the user dismisses (timeout: 0 → persistent).
const SUCCESS_DEFAULT_TIMEOUT_MS = 5000;

const effectiveTimeout = (alert: DocuclipperAlert): number => {
  if (alert.timeout > 0) return alert.timeout;
  if (alert.type === "success") return SUCCESS_DEFAULT_TIMEOUT_MS;
  return 0;
};

export const AlertBanner: React.SFC<Props & ReduxProps> = ({
  alert,
  removeAlert,
}) => {
  const dispatch = useDispatch();

  React.useEffect(() => {
    dispatch(updateTours());
  }, [dispatch, alert]);

  // Auto-dismiss based on effective timeout. Returns cleanup so a new alert
  // replacing the current one doesn't fire a stale timer.
  React.useEffect(() => {
    if (!alert) return;
    const t = effectiveTimeout(alert);
    if (t > 0) {
      const id = setTimeout(() => removeAlert(), t);
      return () => clearTimeout(id);
    }
  }, [alert, removeAlert]);

  if (!alert) {
    return null;
  }
  let color;
  let icon: IconProp = "exclamation-circle";
  switch (alert.type) {
    case "success":
      color = "success";

      break;
    case "error":
      color = "danger";
      break;
    case "info":
      color = "primary";
      break;
    default:
      color = "primary";
      break;
  }

  return (
    <Alert
      dismissible={true}
      key={uuidv4()}
      style={{ marginBottom: 0 }}
      variant={color}
      onClose={() => removeAlert()}
    >
      <FontAwesomeIcon icon={icon} className={`text-${color}`} />

      {alert.messageWithUpdate && (
        <UpgradeAlertBanner message={alert.messageWithUpdate} />
      )}
      {!alert.messageWithUpdate && (
        <span className="ml-2">{alert.message}</span>
      )}
    </Alert>
  );
};

const mapStateToProps = (state) => ({
  alert: state.Alerts.alert,
});

const mapDispatchToProps = {
  removeAlert,
};

export default connect(mapStateToProps, mapDispatchToProps)(AlertBanner);
